"use client";

import { Flame, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScoopHero() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: mockupRef,
    isVisible: mockupVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="5775" id="the-melting-point-hero" className="relative overflow-hidden bg-background pt-40 pb-28 md:pt-52 md:pb-36 font-default">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-[520px] w-[520px] rounded-lg blur-3xl opacity-45" style={{
      background: "hsl(0 100% 71% / 0.2)",
      animation: "pebbleDriftA 14s ease-in-out infinite"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-24 h-[440px] w-[440px] rounded-lg blur-3xl opacity-40" style={{
      background: "hsl(355 100% 96%)",
      animation: "pebbleDriftB 14s ease-in-out infinite"
    }} />

      <style>{`
        @keyframes pebbleDriftA {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(10px, 12px); }
        }
        @keyframes pebbleDriftB {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(-12px, -8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal-anim, .mockup-anim {
            transition: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-[1240px] px-5 md:px-8">
        {}
        <div ref={headlineRef} className="reveal-anim mx-auto flex max-w-[920px] flex-col items-center text-center" style={{
        opacity: headlineVisible ? 1 : 0,
        transform: headlineVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 540ms cubic-bezier(.2,.7,.2,1), transform 540ms cubic-bezier(.2,.7,.2,1)"
      }}>
          {}
          <Badge className="mb-8 rounded-lg border-transparent px-3 py-1.5 text-[0.75rem] font-semibold tracking-wide text-primary" style={{
          backgroundColor: "hsl(355 100% 96%)"
        }}>
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Artisanal · Small Batch · Premium</span>
          </Badge>

          {}
          <h1 className="font-default font-bold text-foreground" style={{
          fontSize: "clamp(2.75rem, 6.4vw, 5.5rem)",
          letterSpacing: "-0.03em",
          lineHeight: 0.98
        }}>
            The art of{" "}
            <span className="font-serif italic font-medium" style={{
            color: "hsl(0 100% 71%)"
          }}>
              frozen indulgence
            </span>
          </h1>

          {}
          <p className="mt-7 max-w-[580px] font-sans text-muted-foreground" style={{
          fontSize: "clamp(1.1rem, 1.25vw, 1.25rem)",
          letterSpacing: "-0.005em",
          lineHeight: 1.55
        }}>
            Hand-crafted ice cream made with locally sourced cream and exotic botanicals. Experience a symphony of textures and flavors in every single scoop.
          </p>

          {}
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
            <Link to="/ShopPage">
              <Button size="lg" className="h-auto bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.04] hover:bg-foreground" style={{
              boxShadow: "0 8px 20px -8px hsl(0 100% 71% / 0.55)",
              transitionTimingFunction: "cubic-bezier(.34,1.56,.64,1)"
            }}>
                Shop our collection
              </Button>
            </Link>
            <Link to="/TheArtisanalProcessPage">
              <Button variant="outline" size="lg" className="h-auto border-2 border-foreground bg-transparent px-7 py-3.5 text-base font-semibold text-foreground hover:bg-foreground hover:text-background">
                Our process
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div ref={mockupRef} className="mockup-anim relative mx-auto mt-20 flex w-full max-w-[420px] justify-center md:mt-24" style={{
        opacity: mockupVisible ? 1 : 0,
        transform: mockupVisible ? "translateY(0)" : "translateY(60px)",
        transition: "opacity 540ms cubic-bezier(.2,.7,.2,1) 240ms, transform 540ms cubic-bezier(.2,.7,.2,1) 240ms"
      }}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-8 bottom-0 h-24 rounded-lg blur-3xl" style={{
          background: "hsl(0 100% 71% / 0.35)"
        }} />

          {}
          <div className="relative w-[78vw] max-w-[340px] md:w-[340px]" style={{
          transform: "rotate(-2deg)"
        }}>
            <div className="relative overflow-hidden border-[10px] p-1" style={{
            borderColor: "hsl(0 0% 12%)",
            background: "hsl(0 0% 12%)",
            boxShadow: "0 30px 60px -20px hsl(0 0% 12% / 0.35)"
          }}>
              <div className="relative overflow-hidden px-5 pt-8 pb-10" style={{
              backgroundColor: "hsl(40 33% 98%)"
            }}>
                {}
                <div className="absolute left-1/2 top-2 h-5 w-24 -translate-x-1/2" style={{
                backgroundColor: "hsl(0 0% 12%)"
              }} />

                {}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[0.7rem] font-semibold uppercase tracking-widest text-muted-foreground">
                    Edition 01
                  </span>
                  <span className="px-2 py-1 text-[0.65rem] font-semibold" style={{
                  backgroundColor: "hsl(0 100% 71% / 0.1)",
                  color: "hsl(0 100% 71%)"
                }}>
                    Limited
                  </span>
                </div>

                {}
                <div className="mt-5 p-5" style={{
                backgroundColor: "hsl(40 33% 95%)",
                boxShadow: "0 1px 0 hsl(0 0% 12% / 0.04), 0 24px 40px -24px hsl(0 0% 12% / 0.12)"
              }}>
                  <p className="text-[0.7rem] font-semibold uppercase tracking-widest text-muted-foreground">
                    Flavor profile
                  </p>
                  <h3 className="mt-2 font-default font-semibold text-foreground" style={{
                  fontSize: "1.25rem",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2
                }}>
                    Madagascar Vanilla
                  </h3>
                  <div className="mt-4 flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden" style={{
                    backgroundColor: "hsl(0 0% 12% / 0.08)"
                  }}>
                      <div className="h-full" style={{
                      width: "100%",
                      backgroundColor: "hsl(0 100% 71%)"
                    }} />
                    </div>
                    <span className="text-xs font-semibold text-foreground">100%</span>
                  </div>
                </div>

                {}
                <div className="mt-6 flex items-center justify-center">
                  <div className="flex items-center gap-2 px-4 py-3" style={{
                  backgroundColor: "hsl(0 100% 71%)",
                  color: "hsl(0 0% 100%)",
                  boxShadow: "0 12px 28px -10px hsl(0 100% 71% / 0.6)"
                }}>
                    <Flame className="h-5 w-5" aria-hidden="true" />
                    <span className="font-default text-sm font-bold">Artisanal Batch</span>
                  </div>
                </div>

                {}
                <div className="mt-6 py-3 text-center text-sm font-semibold" style={{
                backgroundColor: "hsl(0 0% 12%)",
                color: "hsl(40 33% 98%)"
              }}>
                  Add to cart
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}